<template>
  <div class="app-container" :class="{ 'lang-hi': lang === 'hi' }">
    <NavbarBar v-if="showChrome" />
    <BackgroundAudio v-if="showChrome" />
    <main class="main-content" :class="{ 'with-nav': showChrome }">
      <router-view></router-view>
    </main>

    <OnboardingWizard v-if="showOnboarding" @done="showOnboarding = false" />

    <!-- Achievement toast -->
    <transition name="badge-toast" :duration="300">
      <div v-if="currentBadge" class="badge-toast" @click="dismissBadge">
        <div class="bt-icon">{{ currentBadge.icon }}</div>
        <div class="bt-text">
          <div class="bt-title">🏆 {{ lang === 'hi' ? 'नई उपलब्धि!' : 'Achievement unlocked!' }}</div>
          <div class="bt-name">{{ currentBadge.name[lang] }}</div>
          <div class="bt-desc">{{ currentBadge.desc[lang] }}</div>
        </div>
      </div>
    </transition>

    <!-- Level-up celebration -->
    <transition name="celebrate" :duration="320">
      <div v-if="currentCelebration" class="celebrate-overlay" @click.self="dismissCelebration">
        <div class="celebrate-card" role="dialog" aria-modal="true">
          <div class="cc-burst">🎉</div>
          <div class="cc-title">⭐ {{ lang === 'hi' ? 'स्तर' : 'Level' }} {{ currentCelebration.level }}!</div>
          <div class="cc-coins">🪙 +{{ currentCelebration.coins }} {{ lang === 'hi' ? 'सिक्के' : 'coins' }}</div>
          <div class="cc-line">
            {{ lang === 'hi'
              ? 'शाबाश! तुम हर दिन और होशियार हो रहे हो — ऐसे ही सीखते रहो!'
              : 'Amazing work! You are getting smarter every day — keep it up!' }}
          </div>
          <button class="cc-btn" @click="dismissCelebration">
            {{ lang === 'hi' ? 'बढ़िया!' : 'Awesome!' }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import NavbarBar from './components/layout/NavBar.vue';
import BackgroundAudio from './components/layout/BackgroundAudio.vue';
import OnboardingWizard from './components/OnboardingWizard.vue';
import { languageState } from './services/translationService';
import { badgeQueue, celebrationQueue } from './services/game';
import { playWin } from './services/sfx';

export default {
  name: 'App',
  components: {
    NavbarBar,
    BackgroundAudio,
    OnboardingWizard,
  },
  data() {
    return {
      languageState,
      badgeQueue,
      celebrationQueue,
      currentBadge: null,
      badgeTimer: null,
      currentCelebration: null,
      showOnboarding: false
    };
  },
  computed: {
    lang() { return this.languageState.currentLanguage; },
    showChrome() { return this.$route.name !== 'Login'; }
  },
  watch: {
    badgeQueue: {
      handler() { this.showNextBadge(); },
      deep: true
    },
    celebrationQueue: {
      handler() { this.showNextCelebration(); },
      deep: true
    },
    $route: {
      handler() {
        // Guests get onboarding too. It only writes localStorage (language,
        // class, interests), so it works with no account — and without it a
        // visitor silently gets Class 7 content and an unfiltered Home.
        this.showOnboarding =
          this.$route.name !== 'Login' &&
          localStorage.getItem('onboarded') !== '1';
      },
      immediate: true
    }
  },
  methods: {
    showNextBadge() {
      if (this.currentBadge || this.badgeQueue.length === 0) return;
      this.currentBadge = this.badgeQueue.shift();
      playWin();
      this.badgeTimer = setTimeout(() => this.dismissBadge(), 4200);
    },
    dismissBadge() {
      clearTimeout(this.badgeTimer);
      this.currentBadge = null;
      // Show the next one if more badges were earned at once
      this.$nextTick(() => setTimeout(() => this.showNextBadge(), 350));
    },
    // Level-up modal — waits for the student to tap "Awesome!", so it is
    // never auto-dismissed the way the badge toast is.
    showNextCelebration() {
      if (this.currentCelebration || this.celebrationQueue.length === 0) return;
      this.currentCelebration = this.celebrationQueue.shift();
      // Several activities call playWin() themselves right after awardXP()
      // (Math Path, Wonder Lab, the academics quizzes). Delay ours slightly
      // so a level-up plays as a follow-up flourish instead of two win
      // sounds firing on top of each other in the same tick.
      clearTimeout(this._celebrateSfx);
      this._celebrateSfx = setTimeout(() => playWin(), 260);
    },
    dismissCelebration() {
      this.currentCelebration = null;
      // Queue up the next level if several were crossed in one award
      this.$nextTick(() => setTimeout(() => this.showNextCelebration(), 400));
    }
  }
}
</script>

<style>
:root {
  --primary: #58CC02;
  --secondary: #1CB0F6;
  --accent: #FF9600;
  --background: #FFF9F0;
  --text-light: #8E8577;
  --border-c: #E8E0D2;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Baloo 2', 'Segoe UI', system-ui, -apple-system, sans-serif;
  background-color: var(--background);
  color: var(--text);
  line-height: 1.5;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

.main-content.with-nav {
  padding-top: 62px;
}

/* Achievement toast */
.badge-toast {
  position: fixed;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  display: flex;
  align-items: center;
  gap: 14px;
  background: #FFFFFF;
  border: 2px solid #FFC800;
  border-radius: 22px;
  box-shadow: 0 5px 0 #E6A800, 0 16px 40px rgba(90, 70, 30, 0.25);
  padding: 14px 22px;
  cursor: pointer;
  max-width: 92vw;
}
.bt-icon {
  font-size: 2.6rem;
  animation: sv-bounce 0.8s ease infinite;
}
.bt-title {
  font-size: 0.78rem;
  font-weight: 800;
  color: #A87900;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.bt-name { font-size: 1.1rem; font-weight: 800; color: #3C3C3C; }
.bt-desc { font-size: 0.85rem; font-weight: 600; color: #8E8577; }

.badge-toast-enter-active { transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.badge-toast-leave-active { transition: all 0.25s ease; }
.badge-toast-enter-from, .badge-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(40px);
}

/* Level-up celebration modal */
.celebrate-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(60, 48, 26, 0.5);
  backdrop-filter: blur(2px);
}
.celebrate-card {
  width: 100%;
  max-width: 340px;
  background: #FFF9F0;
  border: 3px solid #FFC800;
  border-radius: 26px;
  box-shadow: 0 8px 0 #E6A800, 0 22px 46px rgba(60, 48, 26, 0.32);
  padding: 24px 20px 22px;
  text-align: center;
  font-family: 'Baloo 2', 'Segoe UI', system-ui, sans-serif;
  animation: cc-pop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
/* NOTE: this style block is global (App.vue is unscoped), and ViewsHome's
   "continue" CTA also uses .cc-title — so every rule below is confined to
   .celebrate-card to keep the modal's styling out of other components. */
.celebrate-card .cc-burst {
  font-size: 3rem;
  line-height: 1;
  animation: cc-wiggle 1.1s ease-in-out infinite;
}
.celebrate-card .cc-title {
  margin-top: 6px;
  font-size: 1.9rem;
  font-weight: 800;
  color: #3C3C3C;
  line-height: 1.2;
}
.celebrate-card .cc-coins {
  margin-top: 4px;
  font-size: 1.15rem;
  font-weight: 800;
  color: #E68A00;
}
.celebrate-card .cc-line {
  margin: 10px 4px 18px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #8E8577;
  line-height: 1.45;
}
.celebrate-card .cc-btn {
  width: 100%;
  border: none;
  border-radius: 16px;
  background: #58CC02;
  color: #FFFFFF;
  font-family: inherit;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  padding: 12px 20px;
  cursor: pointer;
  box-shadow: 0 4px 0 #46A302;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
}
.celebrate-card .cc-btn:active {
  transform: translateY(4px);
  box-shadow: 0 0 0 #46A302;
}

@keyframes cc-pop {
  0% { opacity: 0; transform: scale(0.6) translateY(30px); }
  70% { opacity: 1; transform: scale(1.05) translateY(0); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes cc-wiggle {
  0%, 100% { transform: rotate(-8deg) scale(1); }
  50% { transform: rotate(8deg) scale(1.12); }
}

.celebrate-enter-active { transition: opacity 0.28s ease; }
.celebrate-leave-active { transition: opacity 0.22s ease; }
.celebrate-enter-from, .celebrate-leave-to { opacity: 0; }

@media (max-width: 380px) {
  .celebrate-card .cc-title { font-size: 1.6rem; }
  .celebrate-card { padding: 20px 16px 18px; }
}
</style>
